/**
 * monthEndNotification.js
 *
 * One local notification at 8am on the 1st: "Time to close the books on
 * [Month]". No backend - it's queued on the phone each time the app opens, and
 * rebuilt whenever anything that affects it changes.
 *
 * Kept completely separate from Notes & Reminders:
 *   - its own kind tag ('worthly-month-end'), so reminders' cancelOurs() never
 *     sees it and this file never touches a 'worthly-reminder'
 *   - one fixed id above the range reminders can ever use (they stay below
 *     2,000,000,000)
 *   - it never asks for permission; it only schedules if the user has already
 *     said yes (through reminders or the permission prompt)
 */
import { LocalNotifications } from '@capacitor/local-notifications';
import { permissionState } from './reminderNotifications';
import { getReviewState, isReviewEligible, monthKeyOf, monthNameOf, shiftMonthKey } from './monthEndReview';

const KIND = 'worthly-month-end';
const NOTIF_ID = 2100000001; // above reminders' range, below the 32-bit limit
const HOUR = 8;

/** The next 8am on a 1st that is still ahead of us. */
export function nextMonthEndAlert(now = new Date()) {
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1, HOUR, 0, 0, 0);
  if (thisMonth.getTime() > now.getTime()) return thisMonth;
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, HOUR, 0, 0, 0);
}

/** Cancel only our own alert - matched by kind, so a reminder can never be hit. */
async function cancelMonthEnd() {
  try {
    const pending = await LocalNotifications.getPending();
    const ours = (pending.notifications || [])
      .filter((n) => n.extra && n.extra.kind === KIND)
      .map((n) => ({ id: n.id }));
    if (ours.length) await LocalNotifications.cancel({ notifications: ours });
  } catch (e) {
    /* nothing pending, or the plugin isn't available (web) */
  }
}

/**
 * Queue (or clear) the month-end alert. Safe to call as often as we like.
 * Skips it when: notifications aren't allowed, the user wasn't using the app
 * from the start of the month, or they've already opened / skipped that review.
 */
export async function syncMonthEndNotification(userData) {
  if (!userData) return { scheduled: false };

  const at = nextMonthEndAlert();
  const monthKey = shiftMonthKey(monthKeyOf(at), -1); // the month being reviewed
  const s = getReviewState(userData);
  const off = userData?.notification_preferences?.month_end_review === false;
  const done = !!((s.seen && s.seen[monthKey]) || (s.skipped && s.skipped[monthKey]));
  const eligible = isReviewEligible(userData.transactions || [], monthKey);

  if ((await permissionState()) !== 'granted' || off || done || !eligible) {
    await cancelMonthEnd();
    return { scheduled: false };
  }

  await cancelMonthEnd();
  try {
    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIF_ID,
        title: `Time to close the books on ${monthNameOf(monthKey)}! \u{1F389}\u{1F4DA}`,
        body: 'Your month-end review is ready, come see how you did! ✨',
        schedule: { at, allowWhileIdle: true },
        extra: { kind: KIND, monthKey },
      }],
    });
    return { scheduled: true, at, monthKey };
  } catch (e) {
    return { scheduled: false, error: String(e) };
  }
}
